import React, { useContext } from 'react';
import { css } from '@emotion/react';
import { CircularProgress } from '@material-ui/core';
import { FlashOn, ImportExport } from '@material-ui/icons';
import { UserContext } from '@/components/App';
import { pluralize, routingModeFor, ROUTING_AUTO, ROUTING_SHOP } from '@/components/CorporateUsers/shippers/utils';
import {
    AccentBar,
    Card,
    CardBody,
    RoutingCard,
    RoutingGrid,
    RoutingIcon,
    Section,
    SectionHeader,
    SectionNote,
    SectionTitle,
    StatusPill,
    StepBadge,
    StepPill,
    StrongCell,
    SubCell,
    WaterfallLabel,
} from '../blocks';
import { EMPTY_STATE_COPY, ROUTING_WATERFALL } from '../constants';
import { useShipperProfile } from '../hooks';

const Header = ({ title, note }) => (
    <SectionHeader>
        <AccentBar />
        <SectionTitle>{title}</SectionTitle>
        {note && <SectionNote>{note}</SectionNote>}
    </SectionHeader>
);

const row = css`
    align-items: flex-start;
    display: flex;
    gap: 12px;
`;

const Overview = () => {
    // Context has already been overridden to the target shipper by index.jsx.
    const { client } = useContext(UserContext);
    const mode = routingModeFor(client?.shipper_type);
    const businessName = client?.business_name || 'This shipper';
    const { assignment, loading: profileLoading, error: profileError } = useShipperProfile();
    const carrierCount = assignment?.mappedCarrierIds?.size || 0;

    let markets = EMPTY_STATE_COPY.markets;
    if (profileLoading) {
        markets = <CircularProgress size={18} />;
    } else if (profileError) {
        markets = "Couldn't load profiles. Refresh to try again.";
    } else if (assignment?.profileName) {
        markets = (
            <div>
                <StrongCell>{assignment.profileName}</StrongCell>
                <SubCell>
                    {carrierCount} {pluralize(carrierCount, 'carrier')} mapped across{' '}
                    {assignment.profileNames.length > 1 ? "these profiles'" : "the profile's"} subregions
                </SubCell>
            </div>
        );
    }

    return (
        <>
            {/*
                Removed — nothing backs any of them, so each rendered as a titled empty card:
                  · Onboarding checklist  — no per-area completion state exists
                  · What's blocking go-live — no blockers to read
                  · Training — no module/progress record
                They belong here, above Markets awarded, when there is data to put in them.
                See REMOVED_SECTIONS in ../constants.js for what each one showed.
            */}

            <Section>
                <Header
                    title="Markets awarded · profiles & rates"
                    note="· corporate-only — the shipper↔carrier connection, wired on the Map"
                />
                <Card>
                    <CardBody>
                        {markets}
                        {/* "Start on the map →" removed — see REMOVED_ACTIONS in ../constants.js. */}
                    </CardBody>
                </Card>
            </Section>

            <Section>
                <Header
                    title="Order routing"
                    note={`· corporate-only — how ${businessName}'s orders reach a carrier.`}
                />
                <RoutingGrid>
                    <RoutingCard active={mode === ROUTING_AUTO}>
                        <div
                            css={css`
                                align-items: flex-start;
                                display: flex;
                                justify-content: space-between;
                            `}
                        >
                            <div css={row}>
                                <RoutingIcon tone="green">
                                    <FlashOn />
                                </RoutingIcon>
                                <div>
                                    <StrongCell>Auto-assign</StrongCell>
                                    <SubCell>orders route instantly by zip — no human in the loop</SubCell>
                                </div>
                            </div>
                            <StatusPill tone={mode === ROUTING_AUTO ? 'on' : 'off'}>
                                {mode === ROUTING_AUTO ? 'On' : 'Off'}
                            </StatusPill>
                        </div>

                        <div>
                            <WaterfallLabel>Waterfall · first match wins</WaterfallLabel>
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    gap: 14px;
                                    margin-top: 16px;
                                `}
                            >
                                {ROUTING_WATERFALL.map((step, index) => (
                                    <div key={step.title} css={row}>
                                        <StepBadge>{index + 1}</StepBadge>
                                        <div>
                                            <span
                                                css={css`
                                                    align-items: center;
                                                    display: inline-flex;
                                                    gap: 8px;
                                                `}
                                            >
                                                <StrongCell>{step.title}</StrongCell>
                                                {step.badge && <StepPill>{step.badge}</StepPill>}
                                            </span>
                                            <SubCell>{step.detail}</SubCell>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </RoutingCard>

                    <RoutingCard active={mode === ROUTING_SHOP}>
                        <div
                            css={css`
                                align-items: flex-start;
                                display: flex;
                                justify-content: space-between;
                            `}
                        >
                            <div css={row}>
                                <RoutingIcon tone="indigo">
                                    <ImportExport />
                                </RoutingIcon>
                                <div>
                                    <StrongCell>Rate Shop</StrongCell>
                                    <SubCell>every order shows all carriers on the zip — you pick, Kayak-style</SubCell>
                                </div>
                            </div>
                            <StatusPill tone={mode === ROUTING_SHOP ? 'on' : 'off'}>
                                {mode === ROUTING_SHOP ? 'On' : 'Off'}
                            </StatusPill>
                        </div>
                        {/* "Preview rate shop →" removed — see REMOVED_ACTIONS in ../constants.js. */}
                    </RoutingCard>
                </RoutingGrid>
            </Section>

            {/*
                Removed — there is no note storage behind this. `clients.onward_admin_notes` is a
                single text column for Onward admins, not the per-account history log the design
                shows, so wiring it here would mean showing one team's notes to another.
            */}
        </>
    );
};

export default Overview;
